import AdminLogin from '../components/AdminLogin';
import { useAuth } from '../contexts/AuthContext';
import React, { useState, useEffect } from 'react';
import { listRecords, createRecord, deleteRecord, uploadMedia, MARKETPLACE_BUCKET } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Lock, LogIn, X, Plus, Store, Package, ShoppingBag, ChevronLeft, ChevronRight,
    ArrowRight, Image as ImageIcon, Sparkles, Trash2 } from 'lucide-react';
import { FaFacebookF, FaInstagram, FaWhatsapp } from 'react-icons/fa';

// Login Form Component

const MarketplaceProduct = ({ product }) => {
    const stop = (e) => e.stopPropagation();
    const imageUrl = (product.imageUrls && product.imageUrls[0]) || product.imageUrl;
    const imageCount = (product.imageUrls && product.imageUrls.length) || 0;

    return (
        <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100
                        bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5
                        hover:border-purple-100 hover:shadow-xl">
            {/* Media */}
            <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-purple-100 via-purple-50 to-gray-100">
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={product.name}
                        className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                    />
                ) : (
                    <div className="grid h-full place-items-center">
                        <Package className="h-12 w-12 text-purple-300" />
                    </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/25 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                {product.category && (
                    <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-xs
                                     font-semibold capitalize text-purple-700 shadow-sm backdrop-blur">
                        {product.category}
                    </span>
                )}
                {imageCount > 1 && (
                    <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full
                                     bg-black/55 px-2.5 py-1 text-xs font-medium text-white backdrop-blur">
                        <ImageIcon className="h-3.5 w-3.5" />
                        {imageCount}
                    </span>
                )}
            </div>

            {/* Body */}
            <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-lg font-semibold leading-snug text-gray-900
                             transition-colors duration-300 group-hover:text-purple-700">
                    {product.name}
                </h3>
                <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-gray-500">
                    {product.description}
                </p>

                <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4">
                    {product.price ? (
                        <span className="text-xl font-bold text-purple-700">${product.price}</span>
                    ) : (
                        <span className="text-sm font-semibold uppercase tracking-wide text-gray-400">
                            Enquire
                        </span>
                    )}

                    <div className="flex items-center gap-2" onClick={stop}>
                        {product.facebookLink && (
                            <a
                                href={product.facebookLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View on Facebook"
                                className="grid h-8 w-8 place-items-center rounded-full bg-gray-100 text-[#1877F2]
                                         transition-colors hover:bg-[#1877F2] hover:text-white"
                            >
                                <FaFacebookF className="h-4 w-4" />
                            </a>
                        )}
                        {product.instagramLink && (
                            <a
                                href={product.instagramLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View on Instagram"
                                className="grid h-8 w-8 place-items-center rounded-full bg-gray-100 text-[#E4405F]
                                         transition-colors hover:bg-[#E4405F] hover:text-white"
                            >
                                <FaInstagram className="h-4 w-4" />
                            </a>
                        )}
                        {product.whatsappLink && (
                            <a
                                href={`https://wa.me/${product.whatsappLink}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Chat on WhatsApp"
                                className="grid h-8 w-8 place-items-center rounded-full bg-gray-100 text-[#25D366]
                                         transition-colors hover:bg-[#25D366] hover:text-white"
                            >
                                <FaWhatsapp className="h-4 w-4" />
                            </a>
                        )}
                    </div>
                </div>

                <div className="mt-4 flex items-center text-sm font-semibold text-purple-600">
                    View details
                    <ArrowRight className="ml-1.5 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
            </div>
        </div>
    );
};

// Function to make links in text clickable
const renderDescriptionWithLinks = (text) => {
    if (!text) return '';
    
    // Regex to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // Split text by URLs and create an array of text and link elements
    const parts = text.split(urlRegex);
    const matches = text.match(urlRegex) || [];
    
    return parts.map((part, index) => {
        // Every even index is text, odd indices are URLs
        if (index % 2 === 0) {
            return <span key={index}>{part}</span>;
        } else {
            const url = matches[(index - 1) / 2];
            return (
                <a 
                    key={index} 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                    {url}
                </a>
            );
        }
    });
};

const ProductImageGallery = ({ product }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    
    // Parse imageUrls if it exists, otherwise use single imageUrl
    let imageUrls = [];
    if (product.imageUrls) {
        try {
            imageUrls = typeof product.imageUrls === 'string' 
                ? JSON.parse(product.imageUrls) 
                : product.imageUrls;
        } catch (error) {
            console.error('Error parsing imageUrls:', error);
        }
    }
    
    // Fallback to single imageUrl if no imageUrls array or it's empty
    if (!imageUrls.length && product.imageUrl) {
        imageUrls = [product.imageUrl];
    }
    
    if (!imageUrls.length) {
        return <div className="flex aspect-square flex-col items-center justify-center gap-3 rounded-xl bg-purple-50 text-purple-600">
            <Package className="h-12 w-12" aria-hidden="true" />
            <p>No photo provided</p>
        </div>;
    }
    
    const nextImage = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === imageUrls.length - 1 ? 0 : prevIndex + 1
        );
    };
    
    const prevImage = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === 0 ? imageUrls.length - 1 : prevIndex - 1
        );
    };
    
    return (
        <div className="relative rounded-xl overflow-hidden bg-gray-100">
            <div className="aspect-w-1 aspect-h-1">
                <img
                    src={imageUrls[currentIndex]}
                    alt={`${product.name} - Image ${currentIndex + 1}`}
                    className="w-full h-full object-contain"
                />
            </div>
            
            {/* Image navigation controls - only show if multiple images */}
            {imageUrls.length > 1 && (
                <>
                    <button 
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-2 hover:bg-black/70"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button 
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-2 hover:bg-black/70"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                    
                    {/* Image counter indicator */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                        {currentIndex + 1}/{imageUrls.length}
                    </div>
                </>
            )}
            
            {/* Thumbnail gallery */}
            {imageUrls.length > 1 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {imageUrls.map((url, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentIndex(idx)}
                            className={`w-16 h-16 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                                idx === currentIndex ? 'border-blue-500 scale-105' : 'border-transparent opacity-70'
                            }`}
                        >
                            <img 
                                src={url} 
                                alt={`Thumbnail ${idx + 1}`}
                                className="w-full h-full object-cover"
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const Marketplace = () => {
    const [products, setProducts] = useState([]);
    const { isAdmin: isLoggedIn, logout } = useAuth();
    const [showAddForm, setShowAddForm] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [newProduct, setNewProduct] = useState({
        name: '',
        description: '',
        price: '',
        category: 'general',
        facebookLink: '',
        instagramLink: '',
        whatsappLink: '',
        createdAt: new Date().toISOString(),
        createdBy: 'admin'
    });
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [error, setError] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const navigate = useNavigate();

    const categories = [
        { id: 'general', name: 'General', icon: Package },
        { id: 'clothing', name: 'Clothing', icon: ShoppingBag },
        { id: 'crafts', name: 'Crafts', icon: Store }
    ];

    useEffect(() => {
        fetchProducts();

    }, []);



    const fetchProducts = async () => {
        try {
            console.log('Fetching products...');
            const response = { documents: await listRecords('marketplace') };
            console.log('Raw products:', response.documents);

            // Transform products to include proper image URLs
            const transformedProducts = response.documents.map(product => {
                console.log('Processing product:', product);
                
                // Parse imageUrls if it exists
                let imageUrls = [];
                if (product.imageUrls) {
                    try {
                        imageUrls = Array.isArray(product.imageUrls) ? product.imageUrls : JSON.parse(product.imageUrls);
                    } catch (error) {
                        console.error('Error parsing imageUrls:', error);
                    }
                } else if (product.imageUrl) {
                    imageUrls = [product.imageUrl];
                }
                
                return {
                    ...product,
                    imageUrls
                };
            });

            console.log('Transformed products:', transformedProducts);
            setProducts(transformedProducts);
        } catch (error) {
            console.error('Error fetching products:', error);
            setError('Failed to load products');
        }
    };

    const handleLoginSuccess = () => {
        setShowLoginModal(false);
    };

    const handleLogout = async () => {
        await logout();
        setShowAddForm(false);
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        console.log('Files selected:', files);
        setSelectedFiles(files);
    };

    const removeFile = (index) => {
        setSelectedFiles(selectedFiles.filter((file, i) => i !== index));
    };

    const handleAddProduct = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            // Validate required fields
            if (!newProduct.name || !newProduct.description) {
                setError('Name and description are required');
                setIsLoading(false);
                return;
            }

            // Upload images if they exist
            let imageUrls = [];
            if (selectedFiles.length > 0) {
                try {
                    console.log('Selected files:', selectedFiles);
                    console.log('Uploading images to storage bucket: marketplace-images');
                    
                    // Upload each file and collect their URLs
                    for (const file of selectedFiles) {
                        const uploaded = await uploadMedia(MARKETPLACE_BUCKET, file);
                        imageUrls.push(uploaded.url);
                    }
                    
                    console.log('All images uploaded successfully:', imageUrls);
                } catch (error) {
                    console.error('Error uploading images:', error);
                    console.error('Error details:', {
                        name: error.name,
                        message: error.message,
                        code: error.code,
                        response: error.response
                    });
                    setError('Failed to upload images. Please try again.');
                    setIsLoading(false);
                    return;
                }
            }

            // Prepare the product data
            const productData = {
                name: newProduct.name,
                description: newProduct.description,
                createdAt: new Date().toISOString(),
                createdBy: 'admin'
            };

            if (newProduct.price && !isNaN(parseFloat(newProduct.price))) {
                productData.price = parseFloat(newProduct.price);
            }
            if (imageUrls.length > 0) {
                productData.imageUrls = JSON.stringify(imageUrls);
                productData.imageUrl = imageUrls[0]; // Keep primary image for backward compatibility
            }
            if (newProduct.facebookLink?.trim()) {
                productData.facebookLink = newProduct.facebookLink.trim();
            }
            if (newProduct.instagramLink?.trim()) {
                productData.instagramLink = newProduct.instagramLink.trim();
            }
            if (newProduct.whatsappLink?.trim()) {
                productData.whatsappLink = newProduct.whatsappLink.trim();
            }

            console.log('Creating product with data:', productData);

            await createRecord('marketplace', productData);

            

            // Reset form and refresh products
            setNewProduct({
                name: '',
                description: '',
                price: '',
                facebookLink: '',
                instagramLink: '',
                whatsappLink: ''
            });
            setSelectedFiles([]);
            setShowAddForm(false);
            fetchProducts();
        } catch (error) {
            console.error('Error creating product:', error);
            if (error.response) {
                setError(`Failed to add product: ${error.response.message}`);
            } else if (error.message) {
                setError(`Failed to add product: ${error.message}`);
            } else {
                setError('Failed to add product. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleProductClick = (product) => {
        setSelectedProduct(product);
    };

    const handleCloseProduct = () => {
        setSelectedProduct(null);
    };

    const handleDeleteProduct = async (productId) => {
        try { await deleteRecord('marketplace', productId); await fetchProducts(); }
        catch (error) { setError(error.message || 'Failed to delete product'); }
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-16">
            {/* Hero */}
            <header className="relative overflow-hidden bg-gradient-to-br from-purple-900 via-purple-800 to-purple-950 pt-32 pb-28">
                <div className="pointer-events-none absolute inset-0">
                    <div className="absolute -top-10 left-1/4 h-72 w-72 rounded-full bg-purple-500/20 blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 h-64 w-64 rounded-full bg-gold-400/10 blur-3xl" />
                </div>
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10
                                     px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-purple-100 backdrop-blur-sm">
                        <Sparkles className="h-4 w-4" />
                        Young Entrepreneurs Trade Show
                    </span>
                    <h1 className="mt-5 font-display text-5xl sm:text-6xl font-semibold text-white">
                        CCM{' '}
                        <span className="bg-gradient-to-r from-gold-200 to-white bg-clip-text italic text-transparent">
                            Marketplace
                        </span>
                    </h1>
                    <p className="mx-auto mt-5 max-w-2xl text-lg text-purple-100/80">
                        Discover unique items from our community members. From handcrafted goods to
                        local services, find something special from Antigua &amp; Barbuda&rsquo;s young entrepreneurs.
                    </p>
                </div>
            </header>

            {/* Intro card — ABYETS story */}
            <div className="relative z-10 mx-auto -mt-14 mb-14 max-w-5xl px-4 sm:px-6 lg:px-8">
                <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-100 sm:p-10">
                    <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-purple-600">
                        <Store className="h-4 w-4" />
                        Meet ABYETS
                    </span>
                    <p className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg">
                        In 2022 the noteworthy Community Charitable Ministry (CCM) created its youth arm the ABYETS which consists of young persons ranging from age five through eighteen years who display innovation and creative skills; leadership qualities of assertiveness and mastery and ambition in various disciplines – culinary arts, farming, fashion &amp; graphic designing, makeup artistry, jewellery &amp; soap making, sports coaching, tutoring, transportation logistics and entertainment (writing, theatre, dance, music and singing).
                    </p>
                    <p className="mt-5 font-display text-xl font-semibold text-gray-900">
                        It is my pleasure to introduce you to the following ABYETS:
                    </p>
                </div>
            </div>

            {/* Main Marketplace Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section header + admin controls */}
                <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="font-display text-2xl font-semibold text-gray-900">Browse the Marketplace</h2>
                        <p className="text-sm text-gray-500">
                            {products.length} {products.length === 1 ? 'item' : 'items'} from our young entrepreneurs
                        </p>
                    </div>
                    {isLoggedIn ? (
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="flex items-center rounded-full bg-purple-600 px-5 py-2.5 font-semibold
                                         text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-purple-700 hover:shadow-lg"
                            >
                                <Plus className="mr-2 h-5 w-5" />
                                Add Product
                            </button>
                            <button
                                onClick={handleLogout}
                                className="px-4 py-2 text-gray-500 transition-colors hover:text-gray-800"
                            >
                                Logout
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowLoginModal(true)}
                            className="flex items-center self-start rounded-full border border-gray-200 bg-white px-4 py-2
                                     text-sm font-medium text-gray-600 transition-colors hover:border-purple-200 hover:text-purple-700 sm:self-auto"
                        >
                            <Lock className="mr-2 h-4 w-4" />
                            Admin Login
                        </button>
                    )}
                </div>

                {/* No Products Message */}
                {products.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-white py-16 text-center">
                        <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-purple-50">
                            <Package className="h-8 w-8 text-purple-400" />
                        </div>
                        <h3 className="font-display text-xl font-semibold text-gray-800">No products yet</h3>
                        <p className="mt-1 text-gray-500">
                            Check back soon — our young entrepreneurs are just getting started.
                        </p>
                    </div>
                )}

                {/* Products Grid */}
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {products.map((product) => (
                        <div key={product.$id} className="group/card relative">
                            <div onClick={() => handleProductClick(product)} className="h-full cursor-pointer">
                                <MarketplaceProduct product={product} />
                            </div>
                            {isLoggedIn && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteProduct(product.$id);
                                    }}
                                    title="Delete product"
                                    aria-label="Delete product"
                                    className="absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full
                                             bg-white/90 text-red-500 opacity-0 shadow-lg backdrop-blur transition-all
                                             hover:bg-red-500 hover:text-white group-hover/card:opacity-100"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                
                {/* Product Modal */}
                {selectedProduct && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-purple-950/70 p-4 backdrop-blur-sm" onClick={handleCloseProduct}>
                        <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl animate-fade-in-up" onClick={e => e.stopPropagation()}>
                            <button
                                onClick={handleCloseProduct}
                                aria-label="Close"
                                className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full
                                         bg-white/90 text-gray-600 shadow-lg backdrop-blur transition-all duration-300 hover:rotate-90 hover:bg-white"
                            >
                                <X className="h-5 w-5" />
                            </button>
                            <div className="p-6 sm:p-8">
                                <div className="flex flex-col gap-8 md:flex-row">
                                    <div className="md:w-1/2">
                                        {/* Image Gallery */}
                                        <ProductImageGallery product={selectedProduct} />
                                    </div>
                                    <div className="md:w-1/2">
                                        {selectedProduct.category && (
                                            <span className="mb-3 inline-block rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold capitalize text-purple-700">
                                                {selectedProduct.category}
                                            </span>
                                        )}
                                        <h2 className="mb-3 font-display text-3xl font-semibold text-gray-900">{selectedProduct.name}</h2>
                                        {selectedProduct.price && (
                                            <p className="mb-6 text-3xl font-bold text-purple-700">
                                                ${selectedProduct.price}
                                            </p>
                                        )}
                                        <div className="prose max-w-none mb-8 whitespace-pre-wrap text-gray-600">
                                            {renderDescriptionWithLinks(selectedProduct.description)}
                                        </div>
                                        <div className="flex flex-wrap gap-4">
                                            {selectedProduct.facebookLink && (
                                                <a
                                                    href={selectedProduct.facebookLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#1864D9] transition-colors"
                                                >
                                                    <FaFacebookF className="w-5 h-5" />
                                                    View on Facebook
                                                </a>
                                            )}
                                            {selectedProduct.instagramLink && (
                                                <a
                                                    href={selectedProduct.instagramLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 px-4 py-2 bg-[#E4405F] text-white rounded-lg hover:bg-[#D93250] transition-colors"
                                                >
                                                    <FaInstagram className="w-5 h-5" />
                                                    View on Instagram
                                                </a>
                                            )}
                                            {selectedProduct.whatsappLink && (
                                                <a
                                                    href={`https://wa.me/${selectedProduct.whatsappLink}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-lg hover:bg-[#20BD5A] transition-colors"
                                                >
                                                    <FaWhatsapp className="w-5 h-5" />
                                                    Chat on WhatsApp
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Add Product Form Modal */}
                {showAddForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all duration-300">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold mb-6">Add New Item</h2>
                                <button
                                    onClick={() => setShowAddForm(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            {error && (
                                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                                    {error}
                                </div>
                            )}
                            <form onSubmit={handleAddProduct} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={newProduct.name}
                                        onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        name="description"
                                        value={newProduct.description}
                                        onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                                        required
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Price (optional)
                                    </label>
                                    <input
                                        type="number"
                                        name="price"
                                        value={newProduct.price}
                                        onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                                        step="0.01"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category
                                    </label>
                                    <select
                                        value={newProduct.category}
                                        onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        required
                                    >
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Images
                                    </label>
                                    <div className="flex flex-wrap gap-4 mb-2">
                                        {selectedFiles.map((file, index) => (
                                            <div key={index} className="relative">
                                                <img
                                                    src={URL.createObjectURL(file)}
                                                    alt={`Preview ${index + 1}`}
                                                    className="w-24 h-24 object-cover rounded-lg"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(index)}
                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleFileSelect}
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Facebook Link (optional)
                                    </label>
                                    <input
                                        type="url"
                                        name="facebookLink"
                                        value={newProduct.facebookLink}
                                        onChange={(e) => setNewProduct({...newProduct, facebookLink: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Instagram Link (optional)
                                    </label>
                                    <input
                                        type="url"
                                        name="instagramLink"
                                        value={newProduct.instagramLink}
                                        onChange={(e) => setNewProduct({...newProduct, instagramLink: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        WhatsApp Link (optional)
                                    </label>
                                    <input
                                        type="url"
                                        name="whatsappLink"
                                        value={newProduct.whatsappLink}
                                        onChange={(e) => setNewProduct({...newProduct, whatsappLink: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div className="flex justify-end space-x-4 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowAddForm(false)}
                                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className={`rounded-lg bg-purple-600 px-5 py-2 font-semibold text-white shadow-md transition-all duration-200 hover:bg-purple-700 hover:shadow-lg ${
                                            isLoading ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                    >
                                        {isLoading ? 'Adding...' : 'Add Item'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Login Modal */}
                {showLoginModal && (
                    <AdminLogin
                        onSuccess={handleLoginSuccess}
                        onClose={() => setShowLoginModal(false)}
                    />
                )}
            </div>
        </div>
    );
};

export default Marketplace;
